import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  BadgeDollarSign,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  EyeOff,
  Loader2,
  PlayCircle,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import {
  cancelBooking,
  deleteBookingSoft,
  extendBookingToToday,
  getBookingIdsWithPayments,
  ignoreBookingHelper,
  markBookingCompleted,
  markBookingCompletedAndPaid,
  markBookingInProgress,
  unscheduleBooking,
  type BookingHelperIssue,
} from '../lib/bookingHelper';

interface BookingHelperModalProps {
  isOpen: boolean;
  onClose: () => void;
  issues: BookingHelperIssue[];
  /** Called whenever an issue is successfully resolved (to refresh upstream state). */
  onResolved?: (bookingId: string) => void;
}

function formatRange(startDate: string | null, endDate: string | null): string {
  const fmt = (d: string) => {
    try {
      return format(parseISO(d), 'MMM d');
    } catch {
      return d;
    }
  };
  if (startDate && endDate && startDate !== endDate) return `${fmt(startDate)} – ${fmt(endDate)}`;
  const single = startDate ?? endDate;
  return single ? fmt(single) : 'No date';
}

/** How many days out-of-date this booking is, relative to the business "today". */
function daysStale(issue: BookingHelperIssue): number {
  const anchor = issue.issueType === 'stuck_in_progress' ? issue.endDate : issue.startDate;
  if (!anchor) return 0;
  try {
    return Math.max(0, differenceInCalendarDays(parseISO(issue.businessToday), parseISO(anchor)));
  } catch {
    return 0;
  }
}

export default function BookingHelperModal({
  isOpen,
  onClose,
  issues,
  onResolved,
}: BookingHelperModalProps) {
  // Local working copy so resolved rows drop off immediately.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errorFor, setErrorFor] = useState<{ id: string; message: string } | null>(null);
  // Action sheet target + delete double-confirm state
  const [sheetForId, setSheetForId] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Bulk sweep mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bookings that already have money on them (hides "Mark completed & paid")
  const [paidBookingIds, setPaidBookingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || issues.length === 0) return;
    let active = true;
    getBookingIdsWithPayments(issues.map((i) => i.bookingId))
      .then((ids) => { if (active) setPaidBookingIds(ids); })
      .catch((err) => console.error('Booking helper payment lookup failed:', err));
    return () => { active = false; };
  }, [isOpen, issues]);

  // One list, worst offenders first.
  const remaining = useMemo(
    () =>
      issues
        .filter((i) => !resolvedIds.has(i.bookingId))
        .sort((a, b) => daysStale(b) - daysStale(a)),
    [issues, resolvedIds],
  );

  const sheetIssue = remaining.find((i) => i.bookingId === sheetForId) ?? null;

  if (!isOpen) return null;

  const markResolved = (bookingId: string) => {
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(bookingId);
      return next;
    });
    onResolved?.(bookingId);
  };

  const runAction = async (bookingId: string, action: () => Promise<void>) => {
    setBusyId(bookingId);
    setErrorFor(null);
    try {
      await action();
      markResolved(bookingId);
      setSheetForId(null);
      setConfirmingDelete(false);
    } catch (err) {
      console.error('Booking helper action failed:', err);
      setErrorFor({
        id: bookingId,
        message: err instanceof Error && err.message.includes('required documents')
          ? err.message
          : "Couldn't update — try again.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const runBulk = async (action: (bookingId: string) => Promise<void>) => {
    setBulkBusy(true);
    setErrorFor(null);
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      try {
        await action(id);
        markResolved(id);
      } catch (err) {
        console.error('Bulk booking helper action failed for', id, err);
        setErrorFor({ id, message: 'Some bookings could not be updated — the rest were.' });
      }
    }
    setSelectedIds(new Set());
    setBulkBusy(false);
  };

  const toggleSelected = (bookingId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  };

  const allResolved = remaining.length === 0;

  const Row = ({ issue }: { issue: BookingHelperIssue }) => {
    const busy = busyId === issue.bookingId;
    const isStuck = issue.issueType === 'stuck_in_progress';
    const isSelected = selectedIds.has(issue.bookingId);
    const stale = daysStale(issue);
    const displayTitle =
      issue.title?.trim() ||
      (issue.bookingNumber ? `Booking #${issue.bookingNumber}` : 'Booking');
    const hasCustomer = issue.customerName && issue.customerName !== 'Booking';
    const primaryName = hasCustomer ? issue.customerName : displayTitle;
    const secondary = [
      hasCustomer ? displayTitle : null,
      formatRange(issue.startDate, issue.endDate),
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <li>
        <div
          role="button"
          tabIndex={0}
          onClick={() => (bulkMode ? toggleSelected(issue.bookingId) : (setSheetForId(issue.bookingId), setConfirmingDelete(false)))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (bulkMode) toggleSelected(issue.bookingId);
              else setSheetForId(issue.bookingId);
            }
          }}
          className={`rounded-xl border px-3 py-2.5 cursor-pointer transition-all duration-150 active:scale-[0.99] touch-manipulation ${
            isSelected ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-white hover:border-blue-300'
          }`}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <div className="flex items-center gap-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold text-gray-900">{primaryName}</p>
              <p className="truncate text-[12px] text-gray-500">{secondary}</p>
            </div>

            {/* Staleness chip */}
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                stale >= 7 ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isStuck ? `${stale}d past end` : `${stale}d not started`}
            </span>

            {bulkMode ? (
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors ${
                  isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
                }`}
              >
                {isSelected && (
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
            ) : (
              // One-tap primary action for the likely case
              <button
                type="button"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  void runAction(
                    issue.bookingId,
                    isStuck ? () => markBookingCompleted(issue.bookingId) : () => markBookingInProgress(issue.bookingId),
                  );
                }}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-all hover:bg-blue-700 active:scale-95 disabled:opacity-50 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : isStuck ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <PlayCircle className="h-3.5 w-3.5" />
                )}
                {isStuck ? 'Complete' : 'Start'}
              </button>
            )}
          </div>
        </div>
        {errorFor?.id === issue.bookingId ? (
          <p className="mt-1 px-1 text-[11px] text-red-600">{errorFor.message}</p>
        ) : null}
      </li>
    );
  };

  // ── Action sheet for a single booking ──────────────────────
  const sheetActions = sheetIssue
    ? [
        {
          id: 'complete',
          label: 'Mark completed',
          hint: 'The job is done — finalizes the invoice',
          icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
          run: () => markBookingCompleted(sheetIssue.bookingId),
          show: true,
          primary: true,
        },
        {
          id: 'complete_paid',
          label: 'Mark completed & paid',
          hint: 'Done and paid in full — records the payment and marks the invoice paid',
          icon: <BadgeDollarSign className="h-4 w-4 text-emerald-600" />,
          run: () => markBookingCompletedAndPaid(sheetIssue.bookingId),
          // Only offered when no payment exists yet on the booking
          show: !paidBookingIds.has(sheetIssue.bookingId),
        },
        {
          id: 'extend',
          label: 'Extend to today',
          hint: 'Still working on it — moves the end date',
          icon: <CalendarClock className="h-4 w-4 text-blue-600" />,
          run: () => extendBookingToToday(sheetIssue.bookingId, sheetIssue.businessToday),
          show: sheetIssue.issueType === 'stuck_in_progress',
        },
        {
          id: 'start',
          label: 'Mark in progress',
          hint: 'Work has started',
          icon: <PlayCircle className="h-4 w-4 text-blue-600" />,
          run: () => markBookingInProgress(sheetIssue.bookingId),
          show: sheetIssue.issueType === 'overdue_scheduled',
        },
        {
          id: 'unschedule',
          label: 'Unschedule',
          hint: 'Clear the dates, keep the booking to rebook later',
          icon: <CalendarX className="h-4 w-4 text-gray-500" />,
          run: () => unscheduleBooking(sheetIssue.bookingId),
          show: true,
        },
        {
          id: 'cancel',
          label: 'Cancel booking',
          hint: 'Keeps the record, marked cancelled',
          icon: <XCircle className="h-4 w-4 text-gray-500" />,
          run: () => cancelBooking(sheetIssue.bookingId),
          show: true,
        },
        {
          id: 'delete',
          label: confirmingDelete ? 'Tap again to permanently delete' : 'Delete booking',
          hint: confirmingDelete ? null : 'Removes it permanently',
          icon: <Trash2 className="h-4 w-4 text-red-500" />,
          run: () => deleteBookingSoft(sheetIssue.bookingId),
          show: true,
          danger: true,
        },
        {
          id: 'ignore',
          label: "Don't flag this booking again",
          hint: 'Hides it from this helper forever',
          icon: <EyeOff className="h-4 w-4 text-gray-400" />,
          run: () => ignoreBookingHelper(sheetIssue.bookingId),
          show: true,
        },
      ].filter((a) => a.show)
    : [];

  const modalContent = (
    <div
      className="fixed inset-0 flex items-end justify-center bg-black/50 sm:items-center"
      style={{ zIndex: 100600, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[82vh] sm:w-[480px] sm:rounded-2xl"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight text-gray-900">Keep bookings current</h2>
            <p className="truncate text-[12px] text-gray-500">
              {allResolved ? 'All caught up' : `${remaining.length} booking${remaining.length === 1 ? '' : 's'} need attention`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!allResolved && (
              <button
                type="button"
                onClick={() => {
                  setBulkMode((v) => !v);
                  setSelectedIds(new Set());
                }}
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors touch-manipulation ${
                  bulkMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {bulkMode ? 'Done' : 'Select'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 active:scale-95"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {allResolved ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </span>
              <p className="text-sm font-semibold text-gray-800">Everything&apos;s up to date</p>
              <p className="text-xs text-gray-500">Nice work keeping your schedule clean.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {remaining.map((issue) => (
                <Row key={issue.bookingId} issue={issue} />
              ))}
            </ul>
          )}
          {/* Room for the floating bulk bar */}
          {bulkMode && selectedIds.size > 0 ? <div className="h-16" /> : null}
        </div>

        {/* Footer */}
        {allResolved ? (
          <div className="border-t border-gray-100 p-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 active:scale-[0.99]"
            >
              Done
            </button>
          </div>
        ) : null}

        {/* Floating bulk action bar */}
        {bulkMode && selectedIds.size > 0 && (
          <div className="absolute inset-x-3 bottom-3 rounded-xl border border-gray-200 bg-white p-2.5 shadow-xl" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="flex items-center justify-between gap-2">
              <span className="pl-1 text-[12px] font-medium text-gray-500">{selectedIds.size} selected</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => void runBulk((id) => markBookingCompleted(id))}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-all hover:bg-blue-700 active:scale-95 disabled:opacity-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Complete all
                </button>
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => void runBulk((id) => cancelBooking(id))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-95 disabled:opacity-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  Cancel all
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Per-booking action sheet — fixed overlay so it never gets clipped by
            the modal's own height/overflow (short lists made it cut off) */}
        {sheetIssue && !bulkMode && (
          <div
            className="fixed inset-0 flex items-end justify-center bg-black/35 sm:items-center"
            style={{ zIndex: 100610 }}
            onClick={() => {
              setSheetForId(null);
              setConfirmingDelete(false);
            }}
          >
            <div
              className="w-full max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-gray-200 bg-white p-4 sm:w-[440px] sm:rounded-2xl sm:border"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 min-w-0">
                <p className="truncate text-[14px] font-semibold text-gray-900">
                  {sheetIssue.customerName && sheetIssue.customerName !== 'Booking'
                    ? sheetIssue.customerName
                    : sheetIssue.title?.trim() || `Booking #${sheetIssue.bookingNumber ?? ''}`}
                </p>
                <p className="truncate text-[12px] text-gray-500">
                  {(sheetIssue.title?.trim() || (sheetIssue.bookingNumber ? `Booking #${sheetIssue.bookingNumber}` : ''))} · {formatRange(sheetIssue.startDate, sheetIssue.endDate)}
                </p>
              </div>
              <div className="space-y-1.5">
                {sheetActions.map((a) => {
                  const busy = busyId === sheetIssue.bookingId;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        if (a.danger && !confirmingDelete) {
                          setConfirmingDelete(true);
                          return;
                        }
                        void runAction(sheetIssue.bookingId, a.run);
                      }}
                      className={`w-full rounded-[10px] border px-3.5 py-2.5 text-left transition-all duration-150 active:scale-[0.99] disabled:opacity-50 touch-manipulation ${
                        a.danger
                          ? 'border-red-200 bg-red-50/50 hover:bg-red-50'
                          : a.primary
                            ? 'border-blue-500 bg-blue-50/60 hover:bg-blue-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <span className="flex items-center gap-2.5">
                        {a.icon}
                        <span className="min-w-0">
                          <span className={`block text-[13px] font-semibold ${a.danger ? 'text-red-600' : 'text-gray-900'}`}>
                            {a.label}
                          </span>
                          {a.hint ? <span className="block text-[11px] text-gray-500">{a.hint}</span> : null}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {errorFor?.id === sheetIssue.bookingId ? (
                <p className="mt-2 text-[11px] text-red-600">{errorFor.message}</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
